import React from "react";
import { Box, Typography } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { theme } from "../theme/colors";
import { useUser } from "../context/UserContext";

const VISIBILITY_OPTIONS = [
  { value: "public", label: "Public", icon: <PublicIcon sx={{ fontSize: 20 }} />, description: "Anyone can see, including visitors without an account" },
  { value: "followers", label: "Followers", icon: <FavoriteBorderOutlinedIcon sx={{ fontSize: 20 }} />, description: "People who follow you" },
  { value: "following", label: "Following", icon: <PersonAddOutlinedIcon sx={{ fontSize: 20 }} />, description: "People you follow" },
  { value: "both", label: "Both", icon: <PeopleAltOutlinedIcon sx={{ fontSize: 20 }} />, description: "People you follow AND who follow you" },
];

/**
 * Shared visibility + geo-target audience controls for posts and events.
 */
export default function AudienceSettings({
  visibility,
  onVisibilityChange,
  geoTarget = "everyone",
  onGeoTargetChange,
  showGeoTarget = true,
  contentLabel = "post",
}) {
  const { userState, userDistrict } = useUser() || {};

  const geoTargetOptions = [
    { value: "everyone", label: "Everyone", icon: <PublicIcon sx={{ fontSize: 20 }} />, description: "No location filter" },
    {
      value: "state",
      label: "My State",
      icon: <MapOutlinedIcon sx={{ fontSize: 20 }} />,
      description: userState ? `${userState} only` : "Set your address first",
    },
    {
      value: "district",
      label: "My District",
      icon: <LocationOnOutlinedIcon sx={{ fontSize: 20 }} />,
      description: userDistrict ? `${userState}-${userDistrict}` : "Set your address first",
    },
  ];

  const currentVisibility = VISIBILITY_OPTIONS.find((v) => v.value === visibility);
  const currentGeoTarget = geoTargetOptions.find((g) => g.value === geoTarget);

  const getSummary = () => {
    const visibilityText = {
      public: "Everyone",
      followers: "Your followers",
      following: "People you follow",
      both: "Followers and people you follow",
    };
    const geoText = {
      everyone: "",
      state: " in your state",
      district: " in your district",
    };
    const who = visibilityText[visibility] || "Everyone";
    const where = showGeoTarget ? geoText[geoTarget] || "" : "";
    return `${who}${where} can see this ${contentLabel}`;
  };

  const optionCardSx = (isActive, isDisabled = false) => ({
    flex: "1 1 0",
    minWidth: 72,
    minHeight: 44,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    py: 1.5,
    px: 0.5,
    bgcolor: isActive ? `${theme.primary}08` : "#fff",
    borderRadius: "10px",
    border: "1.5px solid",
    borderColor: isActive ? theme.primary : "#e5e7eb",
    cursor: isDisabled ? "not-allowed" : "pointer",
    gap: 0.5,
    opacity: isDisabled ? 0.5 : 1,
    "&:hover": {
      borderColor: isActive ? theme.primary : isDisabled ? "#e5e7eb" : "#d1d5db",
    },
  });

  return (
    <Box>
      <Box
        sx={{
          bgcolor: "#f9fafb",
          borderRadius: "12px",
          p: 2,
          border: `1px solid ${theme.primary}20`,
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a", mb: 1.5 }}>
          Who can see this?{" "}
          <span style={{ color: "#ef4444", fontSize: "1.25em", fontWeight: 600 }}>*</span>
        </Typography>
        <Box role="radiogroup" aria-label="Post visibility" sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {VISIBILITY_OPTIONS.map((option) => {
            const isActive = visibility === option.value;
            return (
              <Box
                key={option.value}
                onClick={() => onVisibilityChange(option.value)}
                role="radio"
                aria-checked={isActive}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onVisibilityChange(option.value);
                  }
                }}
                sx={optionCardSx(isActive)}
              >
                <Box sx={{ color: isActive ? theme.primary : "#334155" }}>{option.icon}</Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? theme.primary : "#0f172a",
                    textAlign: "center",
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Typography sx={{ fontSize: 12, color: "#475569", mt: 1.5, textAlign: "center" }}>
          {currentVisibility?.description}
        </Typography>
      </Box>

      {showGeoTarget && (
        <Box
          sx={{
            bgcolor: "#f9fafb",
            borderRadius: "12px",
            p: 2,
            border: `1px solid ${theme.primary}20`,
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a", mb: 1.5 }}>
            Target audience{" "}
            <span style={{ color: "#ef4444", fontSize: "1.25em", fontWeight: 600 }}>*</span>
          </Typography>
          <Box role="radiogroup" aria-label="Geographic audience" sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {geoTargetOptions.map((option) => {
              const isDisabled =
                (option.value === "state" && !userState) ||
                (option.value === "district" && (!userState || !userDistrict));
              const isActive = geoTarget === option.value;
              return (
                <Box
                  key={option.value}
                  onClick={() => !isDisabled && onGeoTargetChange?.(option.value)}
                  role="radio"
                  aria-checked={isActive}
                  tabIndex={isDisabled ? -1 : 0}
                  aria-disabled={isDisabled}
                  onKeyDown={(e) => {
                    if (isDisabled) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onGeoTargetChange?.(option.value);
                    }
                  }}
                  sx={optionCardSx(isActive, isDisabled)}
                >
                  <Box sx={{ color: isActive ? theme.primary : isDisabled ? "#94a3b8" : "#334155" }}>
                    {option.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? theme.primary : isDisabled ? "#94a3b8" : "#0f172a",
                      textAlign: "center",
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Typography sx={{ fontSize: 12, color: "#475569", mt: 1.5, textAlign: "center" }}>
            {currentGeoTarget?.description}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          bgcolor: `${theme.primary}10`,
          borderRadius: "10px",
          py: 1.5,
          px: 2,
          border: `1px solid ${theme.primary}30`,
          mb: 2,
        }}
      >
        <VisibilityOutlinedIcon sx={{ fontSize: 20, color: theme.primary }} />
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#0f172a", flex: 1 }}>
          {getSummary()}
        </Typography>
      </Box>
    </Box>
  );
}
